import React, { useState, useEffect } from "react";
import openSocket from "../../services/socket-io";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Container from "@material-ui/core/Container";
import Select from "@material-ui/core/Select";
import TextField from "@material-ui/core/TextField";
import { toast } from "react-toastify";

import api from "../../services/api";
import { i18n } from "../../translate/i18n.js";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		alignItems: "center",
		padding: theme.spacing(8, 8, 3),
	},

	paper: {
		padding: theme.spacing(2),
		display: "flex",
		alignItems: "center",
		marginBottom: 12,

	},

	settingOption: {
		marginLeft: "auto",
	},
	margin: {
		margin: theme.spacing(1),
	},

}));

const Settings = () => {
	const classes = useStyles();

	const [settings, setSettings] = useState([]);
	const [n8nWebhookUrl, setN8nWebhookUrl] = useState("");
	const [n8nWebhookBearer, setN8nWebhookBearer] = useState("");

	useEffect(() => {
		const fetchSession = async () => {
			try {
				const { data } = await api.get("/settings");
				setSettings(data);
			} catch (err) {
				toastError(err);
			}
		};
		fetchSession();
	}, []);

	useEffect(() => {
		if (!settings.length) return;
		setN8nWebhookUrl(settings.find(s => s.key === "n8nWebhookUrl")?.value ?? "");
		setN8nWebhookBearer(
			settings.find(s => s.key === "n8nWebhookBearer")?.value ?? ""
		);
	}, [settings]);

	useEffect(() => {
		const socket = openSocket();

		socket.on("settings", data => {
			if (data.action === "update") {
				setSettings(prevState => {
					const aux = [...prevState];
					const settingIndex = aux.findIndex(s => s.key === data.setting.key);
					if (settingIndex >= 0) {
						aux[settingIndex] = {
							...aux[settingIndex],
							value: data.setting.value,
						};
					} else {
						aux.push(data.setting);
					}
					return aux;
				});
				if (data.setting.key === "n8nWebhookUrl") {
					setN8nWebhookUrl(data.setting.value ?? "");
				}
				if (data.setting.key === "n8nWebhookBearer") {
					setN8nWebhookBearer(data.setting.value ?? "");
				}
			}
		});

		return () => {
			socket.disconnect();
		};
	}, []);

	const handleChangeSetting = async e => {
		const selectedValue = e.target.value;
		const settingKey = e.target.name;

		try {
			await api.put(`/settings/${settingKey}`, {
				value: selectedValue,
			});
			toast.success(i18n.t("settings.success"));
		} catch (err) {
			toastError(err);
		}
	};

	const handleBlurTextSetting = async e => {
		const key = e.target.name;
		const value = e.target.value;
		const previous = settings.find(s => s.key === key)?.value ?? "";
		if (value === previous) return;
		try {
			await api.put(`/settings/${key}`, { value });
			setSettings(prev =>
				prev.some(s => s.key === key)
					? prev.map(s => (s.key === key ? { ...s, value } : s))
					: [...prev, { key, value }]
			);
			toast.success(i18n.t("settings.success"));
		} catch (err) {
			toastError(err);
		}
	};

	const getSettingValue = key => {
		const found = settings.find(s => s.key === key);
		return found ? found.value : "";
	};

	return (
		<div className={classes.root}>
			<Container className={classes.container} maxWidth="sm">
				<Typography variant="body2" gutterBottom>
					{i18n.t("settings.title")}
				</Typography>
				<Paper className={classes.paper}>
					<Typography variant="body1">
						{i18n.t("settings.settings.userCreation.name")}
					</Typography>
					<Select
						margin="dense"
						variant="outlined"
						native
						id="userCreation-setting"
						name="userCreation"
						value={
							settings && settings.length > 0 && getSettingValue("userCreation")
						}
						className={classes.settingOption}
						onChange={handleChangeSetting}
					>
						<option value="enabled">
							{i18n.t("settings.settings.userCreation.options.enabled")}
						</option>
						<option value="disabled">
							{i18n.t("settings.settings.userCreation.options.disabled")}
						</option>
					</Select>

				</Paper>

				<Paper className={classes.paper}>
					<Typography variant="body1">
						{i18n.t("settings.settings.customerInteractionMode.name")}
					</Typography>
					<Select
						margin="dense"
						variant="outlined"
						native
						id="customerInteractionMode-setting"
						name="customerInteractionMode"
						value={
							settings && settings.length > 0
								? getSettingValue("customerInteractionMode") || "native"
								: "native"
						}
						className={classes.settingOption}
						onChange={handleChangeSetting}
					>
						<option value="native">
							{i18n.t("settings.settings.customerInteractionMode.options.native")}
						</option>
						<option value="n8n">
							{i18n.t("settings.settings.customerInteractionMode.options.n8n")}
						</option>
					</Select>
				</Paper>

				<Paper className={classes.paper}>
					<TextField
						id="api-token-setting"
						readonly
						label="Token Api"
						margin="dense"
						variant="outlined"
						fullWidth
						value={settings && settings.length > 0 && getSettingValue("userApiToken")}
					/>
				</Paper>

				<Paper className={classes.paper}>
					<TextField
						id="n8n-webhook-url-setting"
						name="n8nWebhookUrl"
						label={i18n.t("settings.settings.n8nWebhook.urlLabel")}
						placeholder="http://localhost:5678/webhook-test/..."
						margin="dense"
						variant="outlined"
						fullWidth
						value={n8nWebhookUrl}
						onChange={e => setN8nWebhookUrl(e.target.value)}
						onBlur={handleBlurTextSetting}
						helperText={i18n.t("settings.settings.n8nWebhook.urlHelper")}
					/>
				</Paper>

				<Paper className={classes.paper}>
					<TextField
						id="n8n-webhook-bearer-setting"
						name="n8nWebhookBearer"
						label={i18n.t("settings.settings.n8nWebhook.bearerLabel")}
						margin="dense"
						variant="outlined"
						fullWidth
						type="password"
						autoComplete="new-password"
						value={n8nWebhookBearer}
						onChange={e => setN8nWebhookBearer(e.target.value)}
						onBlur={handleBlurTextSetting}
						helperText={i18n.t("settings.settings.n8nWebhook.bearerHelper")}
					/>
				</Paper>

			</Container>
		</div>
	);
};

export default Settings;
